
import { CalendarEvent, ScheduleAssistantResponse, DateTimeRange } from '@/types/calendar';
import { addDays, addMinutes, setHours, setMinutes, isBefore, isAfter, isWeekend, isWithinInterval, format, getDay } from 'date-fns';

interface ScheduleRequest {
  title: string;
  type: string;
  description: string;
  durationMinutes: number;
  participants: string[];
  preferredDays: string[];
  preferredTimeRange: string;
}

// Helper to convert day names to day numbers (0 = Sunday, 1 = Monday, etc.)
const dayNameToNumber = (dayName: string): number => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days.findIndex(day => day === dayName);
};

// Helper to get preferred time ranges based on selection
const getPreferredHours = (timeRange: string): { start: number; end: number } => {
  switch (timeRange) {
    case 'morning':
      return { start: 8, end: 12 };
    case 'afternoon':
      return { start: 12, end: 17 };
    case 'evening':
      return { start: 17, end: 21 };
    default:
      return { start: 9, end: 19 }; // Default business hours
  }
};

export const generateScheduleSuggestions = async (
  existingEvents: CalendarEvent[],
  request: ScheduleRequest
): Promise<ScheduleAssistantResponse> => {
  // This is a simplified mock implementation
  // In a real implementation, this would call an AI service
  
  const now = new Date();
  const suggestedTimeSlots: DateTimeRange[] = [];
  let message = '';
  let reasoning = '';
  
  // Get preferred days as day numbers
  const preferredDayNumbers = request.preferredDays.length > 0
    ? request.preferredDays.map(day => dayNameToNumber(day))
    : [1, 2, 3, 4, 5]; // Default to weekdays
  
  // Get preferred hours
  const preferredHours = getPreferredHours(request.preferredTimeRange);
  
  // Check if the event is longer than 2 hours - different strategy
  const isLongEvent = request.durationMinutes > 120;
  
  // Simple scheduling algorithm
  // Look for available slots in the next 14 days
  for (let dayOffset = 1; dayOffset <= 14; dayOffset++) {
    const potentialDate = addDays(now, dayOffset);
    const dayNumber = getDay(potentialDate);
    
    // Skip if not a preferred day
    if (!preferredDayNumbers.includes(dayNumber)) {
      continue;
    }
    
    // For all-day or long events, prioritize weekends
    if (isLongEvent && !isWeekend(potentialDate) && preferredDayNumbers.includes(0) && preferredDayNumbers.includes(6)) {
      continue; // Skip weekdays for long events if weekends are preferred
    }
    
    // Check for available time slots
    const startHour = preferredHours.start;
    const endHour = preferredHours.end;
    
    // Try different start times within the preferred hours
    for (let hour = startHour; hour <= endHour - Math.ceil(request.durationMinutes / 60); hour++) {
      // Try different minute offsets (0, 15, 30, 45)
      for (let minute of [0, 15, 30, 45]) {
        const potentialStart = setMinutes(setHours(potentialDate, hour), minute);
        const potentialEnd = addMinutes(potentialStart, request.durationMinutes);
        
        // Check if this slot conflicts with any existing event
        const hasConflict = existingEvents.some(event => {
          const eventStart = new Date(event.start);
          const eventEnd = new Date(event.end);
          
          return (
            (isWithinInterval(potentialStart, { start: eventStart, end: eventEnd }) || 
             isWithinInterval(potentialEnd, { start: eventStart, end: eventEnd }) ||
             (isBefore(potentialStart, eventStart) && isAfter(potentialEnd, eventEnd)))
          );
        });
        
        if (!hasConflict) {
          suggestedTimeSlots.push({
            start: potentialStart,
            end: potentialEnd
          });
          
          // Once we find 3 slots, break out
          if (suggestedTimeSlots.length >= 3) {
            break;
          }
        }
      }
      
      if (suggestedTimeSlots.length >= 3) {
        break;
      }
    }
    
    if (suggestedTimeSlots.length >= 3) {
      break;
    }
  }
  
  // Generate appropriate messages based on the request and results
  if (suggestedTimeSlots.length === 0) {
    message = "I couldn't find any available time slots based on your preferences.";
    reasoning = "There seem to be conflicts with existing events during your preferred times. Try expanding your preferred days or time ranges.";
    
    // Add some fallback suggestions anyway
    const tomorrow = addDays(now, 1);
    suggestedTimeSlots.push({
      start: setMinutes(setHours(tomorrow, 9), 0),
      end: setMinutes(setHours(tomorrow, 9 + Math.ceil(request.durationMinutes / 60)), 0)
    });
    
    suggestedTimeSlots.push({
      start: setMinutes(setHours(addDays(now, 2), 14), 0),
      end: setMinutes(setHours(addDays(now, 2), 14 + Math.ceil(request.durationMinutes / 60)), 0)
    });
    
    suggestedTimeSlots.push({
      start: setMinutes(setHours(addDays(now, 3), 11), 0),
      end: setMinutes(setHours(addDays(now, 3), 11 + Math.ceil(request.durationMinutes / 60)), 0)
    });
  } else {
    message = `Here are ${suggestedTimeSlots.length} suggested time slots for your ${request.type}.`;
    
    let reasoningFactors = [];
    
    if (request.preferredDays.length > 0) {
      reasoningFactors.push(`preferred days (${request.preferredDays.join(', ')})`);
    }
    
    if (request.preferredTimeRange) {
      reasoningFactors.push(`preferred time of day (${request.preferredTimeRange})`);
    }
    
    if (request.participants.length > 0) {
      reasoningFactors.push(`${request.participants.length} participant schedules`);
    }
    
    reasoningFactors.push(`event duration (${request.durationMinutes} minutes)`);
    
    reasoning = `I've found these slots based on your ${reasoningFactors.join(', ')} and avoiding conflicts with your ${existingEvents.length} existing events. The earliest available slot is on ${format(suggestedTimeSlots[0].start, 'EEEE, MMMM d')}.`;
  }
  
  return {
    suggestedTimeSlots,
    message,
    reasoning
  };
};
